import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeTable } from '../lib/realtime';

export type IncomeByAppRow = { app: string; total: number; trips: number };

export function useIncomeByApp(driverId: string | undefined) {
  const [rows, setRows] = useState<IncomeByAppRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!driverId) return;
    const { data } = await supabase
      .from('trips')
      .select('platform, fare')
      .eq('driver_id', driverId)
      .eq('status', 'completed')
      .not('fare', 'is', null);

    const byApp = new Map<string, { total: number; trips: number }>();
    for (const row of (data ?? []) as { platform: string | null; fare: number }[]) {
      const app = row.platform ?? 'Direkt';
      const agg = byApp.get(app) ?? { total: 0, trips: 0 };
      agg.total += Number(row.fare);
      agg.trips += 1;
      byApp.set(app, agg);
    }

    setRows(
      [...byApp.entries()]
        .map(([app, v]) => ({ app, total: v.total, trips: v.trips }))
        .sort((a, b) => b.total - a.total)
    );
    setLoading(false);
  }, [driverId]);

  useEffect(() => {
    if (!driverId) {
      setLoading(false);
      return;
    }
    refetch();
    const unsubTrips = subscribeTable('trips', refetch, `driver_id=eq.${driverId}`);
    const unsubTx = subscribeTable('transactions', refetch, `driver_id=eq.${driverId}`);
    return () => {
      unsubTrips();
      unsubTx();
    };
  }, [driverId, refetch]);

  return { rows, loading, refetch };
}
